import { useState } from "react";

import { Input } from "@/components/ui/input/input";
import { GeneralModal } from "@/features/modals/general-modal";

import { useCreateInstallation } from "../../hooks/useMapEntityCreates";
import { useNameBackedExternalId } from "../../hooks/useNameBackedExternalId";
import { createAtLocationDescription } from "./shared/create-at-location-description";

export type CreateInstallationFormProps = {
	locationId: string;
	locationName?: string;
	onClose: () => void;
	onCreated?: (installationId: string) => void;
};

export const CreateInstallationForm = ({
	locationId,
	locationName,
	onClose,
	onCreated,
}: CreateInstallationFormProps) => {
	const createInstallation = useCreateInstallation();
	const { externalId, name, onExternalIdChange, onNameChange } =
		useNameBackedExternalId(locationName ?? "");
	const [error, setError] = useState<null | string>(null);

	const canSubmit =
		externalId.trim().length > 0 && !createInstallation.isPending;

	const handleSubmit = () => {
		if (!canSubmit) return;
		setError(null);
		createInstallation.mutate(
			{
				externalId: externalId.trim(),
				location: locationId,
			},
			{
				onError: () => {
					setError("Could not create installation.");
				},
				onSuccess: (created) => {
					onCreated?.(created.id);
					onClose();
				},
			},
		);
	};

	return (
		<GeneralModal
			closeOnSubmit={false}
			description={createAtLocationDescription(locationName, {
				fallback: "Create a Lookout installation for this location.",
				withLocation: (location) => (
					<>Create a Lookout installation for {location}.</>
				),
			})}
			isOpen
			onClose={onClose}
			onSubmit={handleSubmit}
			submitDisabled={!canSubmit}
			submitText={
				createInstallation.isPending ? "Creating…" : "Create installation"
			}
			title="Create installation"
		>
			<div className="flex flex-col gap-4 pb-2">
				<Input
					label="Name"
					onChange={onNameChange}
					placeholder="Site installation"
					value={name}
				/>
				<Input
					hint="Auto-filled from the name; edit anytime."
					isRequired
					label="External ID"
					onChange={onExternalIdChange}
					placeholder="lookout-site-001"
					value={externalId}
				/>
				{error ? <p className="text-sm text-error-primary">{error}</p> : null}
			</div>
		</GeneralModal>
	);
};
